<template>
  <v-col
    v-if="activeFilter"
    id="validation-filter-controls"
    cols="auto"
    class="pa-0 align-self-center d-flex align-center"
  >
    <v-btn-group divided class="align-center">
      <v-btn
        v-if="modelValue"
        variant="outlined"
        height="28"
        :class="buttonClass"
        @click="emit('update:modelValue', null)"
      >
        {{ $t('actions.showAllRows') }}
      </v-btn>

      <v-btn v-else variant="outlined" height="28" :class="buttonClass" @click="applyActiveFilter">
        {{ $t(activeFilter.label) }}
      </v-btn>

      <v-menu location="bottom end">
        <template #activator="{ props: menuProps }">
          <v-btn
            icon="mdi-menu-down"
            aria-label="Validation filter options"
            variant="outlined"
            height="28"
            :class="buttonClass"
            v-bind="menuProps"
          />
        </template>

        <v-list density="compact">
          <!-- You want it to be disabled if we're currently viewing errors, or there are none -->
          <v-list-item
            :disabled="modelValue === severity.error || !hasErrors"
            @click="emit('update:modelValue', severity.error)"
          >
            <v-list-item-title>
              {{ $t('actions.showErrors') }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            :disabled="modelValue === severity.warn || !hasWarnings"
            @click="emit('update:modelValue', severity.warn)"
          >
            <v-list-item-title>
              {{ $t('actions.showWarnings') }}
            </v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item :disabled="!modelValue" @click="emit('update:modelValue', null)">
            <v-list-item-title>
              {{ $t('actions.showAllRows') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-btn-group>
  </v-col>
</template>

<script setup>
import { computed } from 'vue';
import severity, { severityOrder } from '@/validation/severity';

const props = defineProps({
  hasErrors: {
    type: Boolean,
    required: true,
  },
  hasWarnings: {
    type: Boolean,
    required: true,
  },
  modelValue: {
    type: String,
    default: null,
    validator: value => value === null || severityOrder.includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);

const activeFilter = computed(() => {
  if (props.modelValue) {
    return { mode: props.modelValue };
  }
  if (props.hasErrors) {
    return {
      mode: severity.error,
      label: 'actions.showErrors',
    };
  }
  if (props.hasWarnings) {
    return {
      mode: severity.warn,
      label: 'actions.showWarnings',
    };
  }
  return null;
});

function applyActiveFilter() {
  emit('update:modelValue', activeFilter.value.mode);
}

const buttonClass = computed(() => {
  if (props.modelValue) return 'validation-filter--selected';
  return props.hasErrors ? 'validation-filter--error' : 'validation-filter--warning';
});
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.v-btn .v-icon) {
  color: inherit !important;
}

.validation-filter--selected {
  color: $assortment-primary-colour !important;
  border-color: $assortment-primary-colour !important;
}

.validation-filter--error {
  color: $rtls-red-70 !important;
  border-color: $rtls-red-70 !important;
}

.validation-filter--warning {
  color: $rtls-orange-40 !important;
  border-color: $rtls-orange-40 !important;
}
</style>
